import type { DefaultError, QueryClient, QueryKey } from "@tanstack/react-query";
import { type UseSuspenseQueryOptions, type UseSuspenseQueryResult, useSuspenseQuery } from "@tanstack/react-query";
import { useDeferredValue } from "react";
import { useDeepCompareMemo } from "use-deep-compare";

export function useSuspenseQueryDeferred<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
    options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { isDeferring: boolean } {
    // 👇 Memo the query key 👇
    const queryKey = useDeepCompareMemo(() => options.queryKey, [options.queryKey]);

    const deferredQueryKey = useDeferredValue(queryKey);

    const query = useSuspenseQuery(
        {
            ...options,
            queryKey: deferredQueryKey,
        },
        queryClient
    );

    const result = query as UseSuspenseQueryResult<TData, TError> & { isDeferring: boolean };
    result.isDeferring = queryKey !== deferredQueryKey;

    return result;
}
